import type { Role } from "@webyonetim/contracts";
import * as Dialog from "radix-ui/dialog";
import { useEffect, useState, type SyntheticEvent } from "react";

import { Button, SelectField, TextField } from "../../components/ui/Controls";
import { AlertBanner } from "../../components/ui/Feedback";
import {
  DestructiveConfirmDialog,
  OneTimeSecret,
  ReauthenticationDialog,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage } from "../../lib/auth";
import { securityApi, teamApi } from "../../lib/api";

const roles: readonly {
  readonly code: Role;
  readonly label: string;
  readonly description: string;
}[] = [
  { code: "owner", label: "Sahip", description: "Tüm ayarları ve sahipliği yönetir" },
  { code: "admin", label: "Yönetici", description: "Ekip ve sistem ayarlarını yönetir" },
  { code: "operator", label: "Operatör", description: "İzin verilen operasyonları yürütür" },
  { code: "viewer", label: "Görüntüleyici", description: "Yalnızca yetkili bilgileri görüntüler" },
];

export function InviteMemberDialog({
  onCreated,
  onOpenChange,
  open,
}: {
  readonly onCreated: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [inviteUrl, setInviteUrl] = useState<string>();
  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole("viewer");
      setError(undefined);
      setInviteUrl(undefined);
    }
  }, [open]);
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const response = await teamApi.invite({ email, role });
      onCreated();
      if (response.delivery.mode === "manual") setInviteUrl(response.delivery.inviteUrl);
      else onOpenChange(false);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  if (inviteUrl !== undefined)
    return (
      <OneTimeSecret
        acknowledgement="Davet bağlantısını güvenli biçimde paylaştım"
        body="Bu davet bağlantısı yalnızca şimdi gösterilecek."
        closeLabel="Davet bağlantısı penceresini kapat"
        copyLabel="Davet bağlantısını kopyala"
        heading="Davet bağlantısı hazır"
        onClose={() => {
          setInviteUrl(undefined);
          onOpenChange(false);
        }}
        secret={inviteUrl}
      />
    );
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title>Ekip üyesi davet et</Dialog.Title>
          <Dialog.Description>
            E-posta adresini ve sabit rolü seçin. Etkin izinler API tarafından belirlenir.
          </Dialog.Description>
          <form
            className="form-stack"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <TextField
              autoComplete="email"
              label="E-posta"
              onChange={(event) => {
                setEmail(event.currentTarget.value);
              }}
              required
              type="email"
              value={email}
            />
            <SelectField
              label="Rol"
              onChange={(event) => {
                setRole(event.currentTarget.value as Role);
              }}
              required
              value={role}
            >
              {roles.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label} — {entry.description}
                </option>
              ))}
            </SelectField>
            {error === undefined ? null : (
              <AlertBanner heading="Davet oluşturulamadı" tone="error">
                <p>{error}</p>
              </AlertBanner>
            )}
            <div className="form-actions">
              <Button busy={busy} busyLabel="Davet oluşturuluyor…" type="submit">
                Davet oluştur
              </Button>
              <Dialog.Close asChild>
                <Button variant="secondary">Daveti iptal et</Button>
              </Dialog.Close>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function RemoveMemberFlow({
  email,
  membershipId,
  onClosed,
  onRemoved,
  open,
  requireSecondFactor,
}: {
  readonly email: string;
  readonly membershipId: string;
  readonly onClosed: () => void;
  readonly onRemoved: () => void;
  readonly open: boolean;
  readonly requireSecondFactor: boolean;
}) {
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [reauthError, setReauthError] = useState<string>();
  const [removeError, setRemoveError] = useState<string>();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) {
      setReauthenticationId(undefined);
      setReauthError(undefined);
      setRemoveError(undefined);
    }
  }, [open]);
  const verify = async (credentials: ReauthenticationCredentials) => {
    setBusy(true);
    setReauthError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setReauthError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (reauthenticationId === undefined) return;
    setBusy(true);
    setRemoveError(undefined);
    try {
      await teamApi.removeMember(membershipId, email, reauthenticationId);
      onRemoved();
      onClosed();
    } catch (caught) {
      setRemoveError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  if (reauthenticationId === undefined)
    return (
      <ReauthenticationDialog
        actionName={`${email} ekip üyesini çıkar`}
        error={reauthError}
        onOpenChange={(next) => {
          if (!next) onClosed();
        }}
        onVerify={verify}
        open={open}
        requireSecondFactor={requireSecondFactor}
        submitting={busy}
      />
    );
  return (
    <DestructiveConfirmDialog
      confirmationLabel={email}
      consequence={`“${email}” ekip üyesinin kuruluma erişimi hemen kaldırılacak.`}
      error={removeError}
      finalAction="Ekipten çıkar"
      name="Ekip üyesini çıkar"
      onConfirm={remove}
      onOpenChange={(next) => {
        if (!next) onClosed();
      }}
      open={open}
      submitting={busy}
    />
  );
}

export { roles as teamRoleDefinitions };
